import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";

export default function Consent() {
  const navigate = useNavigate();
  const { consent, logout } = useAuth();
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!checked) {
      toast.error("Please tick the consent box to continue.");
      return;
    }
    setLoading(true);
    try {
      await consent();
      navigate("/care", { replace: true });
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const cancel = async () => {
    await logout();
    navigate("/welcome", { replace: true });
  };

  return (
    <div className="min-h-screen w-full flex justify-center bg-[#E9EEF4]">
      <div className="relative w-full max-w-[430px] min-h-screen bg-[#F8FAFC] flex flex-col px-6 py-8">
        <div className="w-14 h-14 rounded-2xl bg-[#E8F5F1] flex items-center justify-center mb-5">
          <ShieldCheck size={28} className="text-[#1D9E75]" />
        </div>
        <h1 className="font-head text-3xl font-extrabold text-[#0C1B33]">One quick consent</h1>
        <p className="text-slate-600 mt-3 leading-relaxed">
          PocketDocta uses your health profile to provide specialist second opinions and health
          guidance. Before you continue, we need your explicit consent to process this sensitive
          information.
        </p>

        <div className="mt-6 bg-white border border-slate-200 rounded-xl p-4 flex gap-3">
          <Checkbox
            id="consent"
            data-testid="consent-checkbox"
            checked={checked}
            onCheckedChange={(v) => setChecked(!!v)}
            className="mt-1 h-6 w-6 shrink-0 data-[state=checked]:bg-[#1D9E75] data-[state=checked]:border-[#1D9E75]"
          />
          <Label htmlFor="consent" className="text-sm leading-relaxed text-slate-700 font-normal cursor-pointer">
            I consent to PocketDocta processing my health information to provide specialist second
            opinions and health guidance. This is sensitive data and is stored encrypted.
          </Label>
        </div>

        <div className="mt-auto pt-8 space-y-3">
          <Button
            data-testid="consent-continue"
            onClick={submit}
            disabled={loading}
            className="w-full min-h-[56px] rounded-xl bg-[#1D9E75] hover:bg-[#16825F] text-white text-lg font-semibold"
          >
            {loading ? "Please wait…" : "I consent — continue"}
          </Button>
          <Button
            data-testid="consent-cancel"
            onClick={cancel}
            variant="ghost"
            className="w-full min-h-[48px] rounded-xl text-slate-600"
          >
            Cancel and sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
